import { supabase } from '../supabase';

// Tipos de permisos:
// 'view' - Solo puede ver (para playlists públicas)
// 'comment' - Puede ver, comentar y calificar (no puede editar la playlist)
// 'collaborate' - Puede editar, agregar/eliminar canciones, comentar y calificar

export const playlistPermissionsService = {
  // Compartir playlist con un usuario
  async sharePlaylist(playlistId, ownerUserId, sharedWithEmail, permissionType) {
    // Buscar el usuario por email en profiles
    let userData = null;
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('id')
      .eq('email', sharedWithEmail)
      .single();

    if (profileData) {
      userData = profileData;
    } else {
      // Si no existe en profiles, buscarlo en auth.users y crear el perfil
      const { data: authData, error: authError } = await supabase
        .rpc('get_user_by_email', { user_email: sharedWithEmail });

      if (authError || !authData || authData.length === 0) {
        throw new Error('Usuario no encontrado. Verifica que el email esté registrado en la aplicación.');
      }

      // Crear perfil para este usuario
      const { data: newProfile, error: createError } = await supabase
        .from('profiles')
        .insert({
          id: authData[0].id,
          email: sharedWithEmail,
          avatar_url: authData[0].raw_user_meta_data?.avatar_url
        })
        .select()
        .single();

      if (createError) {
        throw new Error('Error creando perfil de usuario');
      }

      userData = newProfile;
    }

    if (!userData) {
      throw new Error('Usuario no encontrado');
    }

    // Verificar que no sea el mismo owner
    if (userData.id === ownerUserId) {
      throw new Error('No puedes compartir la playlist contigo mismo');
    }

    // Insertar o actualizar el permiso
    const { data, error } = await supabase
      .from('playlist_permissions')
      .upsert({
        playlist_id: playlistId,
        user_id: userData.id,
        permission_type: permissionType,
        granted_by: ownerUserId,
        granted_at: new Date().toISOString()
      }, {
        onConflict: 'playlist_id,user_id'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Obtener permisos de una playlist
  async getPlaylistPermissions(playlistId) {
    const { data, error } = await supabase
      .from('playlist_permissions')
      .select(`
        *,
        profiles:user_id (
          id,
          email,
          username,
          avatar_url
        )
      `)
      .eq('playlist_id', playlistId);

    if (error) throw error;
    return data;
  },

  // Obtener permiso de un usuario específico para una playlist
  async getUserPermission(playlistId, userId) {
    const { data, error } = await supabase
      .from('playlist_permissions')
      .select('permission_type')
      .eq('playlist_id', playlistId)
      .eq('user_id', userId)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data?.permission_type || null;
  },

  // Verificar si un usuario tiene permiso para editar
  async canUserEdit(playlistId, userId) {
    // Verificar si es el owner
    const { data: playlist } = await supabase
      .from('playlist')
      .select('user_id')
      .eq('id', playlistId)
      .single();

    if (playlist?.user_id === userId) return true;

    // Verificar si tiene permiso de colaboración
    const permission = await this.getUserPermission(playlistId, userId);
    return permission === 'collaborate';
  },

  // Verificar si un usuario puede comentar/calificar
  async canUserComment(playlistId, userId) {
    // Verificar si es el owner
    const { data: playlist } = await supabase
      .from('playlist')
      .select('user_id, is_public')
      .eq('id', playlistId)
      .single();

    if (playlist?.user_id === userId) return true;

    // Si la playlist es pública, puede ver pero no comentar (solo el owner)
    // A menos que tenga permiso explícito
    const permission = await this.getUserPermission(playlistId, userId);
    return permission === 'comment' || permission === 'collaborate';
  },

  // Verificar si un usuario puede ver una playlist
  async canUserView(playlistId, userId) {
    const { data: playlist } = await supabase
      .from('playlist')
      .select('user_id, is_public')
      .eq('id', playlistId)
      .single();

    // Si es el owner, siempre puede ver
    if (playlist?.user_id === userId) return true;

    // Si es pública, cualquiera puede ver
    if (playlist?.is_public) return true;

    // Verificar si tiene algún permiso
    const permission = await this.getUserPermission(playlistId, userId);
    return !!permission;
  },

  // Revocar permiso de un usuario
  async revokePermission(playlistId, userId, ownerUserId) {
    // Verificar que quien revoca sea el owner
    const { data: playlist } = await supabase
      .from('playlist')
      .select('user_id')
      .eq('id', playlistId)
      .single();

    if (playlist?.user_id !== ownerUserId) {
      throw new Error('Solo el creador puede revocar permisos');
    }

    const { error } = await supabase
      .from('playlist_permissions')
      .delete()
      .eq('playlist_id', playlistId)
      .eq('user_id', userId);

    if (error) throw error;
    return true;
  },

  // Desvincular usuario de una playlist compartida (el propio usuario se desvinca)
  async unlinkSelfFromPlaylist(playlistId, userId) {
    const { error } = await supabase
      .from('playlist_permissions')
      .delete()
      .eq('playlist_id', playlistId)
      .eq('user_id', userId);

    if (error) throw error;
    return true;
  },

  // Obtener todas las playlists compartidas con un usuario
  async getSharedWithUser(userId) {
    console.log('🔍 [Service] Buscando playlists compartidas para usuario:', userId);

    const { data, error } = await supabase
      .from('playlist_permissions')
      .select(`
        id,
        permission_type,
        granted_at,
        playlist:playlist_id (
          id,
          name,
          description,
          image_url,
          is_public,
          user_id,
          created_at,
          updated_at,
          playlist_tracks (
            id,
            track_title,
            track_artist,
            track_image,
            track_preview_url,
            track_external_url,
            position,
            created_at
          )
        )
      `)
      .eq('user_id', userId);

    console.log('🔍 [Service] Resultado query playlist_permissions:', { data, error });

    if (error) {
      console.error('❌ [Service] Error en query:', error);
      throw error;
    }

    // Filtrar los permisos que apuntan a playlists eliminadas
    const validData = data.filter(item => {
      if (!item.playlist) {
        console.warn('⚠️ [Service] Permiso sin playlist (playlist eliminada):', item.id);
        return false;
      }
      return true;
    });

    console.log('🔍 [Service] Playlists válidas después de filtrar:', validData.length);

    // Si no hay playlists compartidas válidas, retornar array vacío
    if (validData.length === 0) {
      console.log('ℹ️ [Service] No hay playlists compartidas con este usuario');
      return [];
    }

    // Ahora obtenemos la información del owner para cada playlist
    const playlistsWithOwners = await Promise.all(
      validData.map(async (item) => {
        // Obtener información del owner
        const { data: ownerData } = await supabase
          .from('profiles')
          .select('id, email, username, avatar_url')
          .eq('id', item.playlist.user_id)
          .single();

        return {
          id: item.playlist.id,
          name: item.playlist.name,
          description: item.playlist.description,
          image: item.playlist.image_url,
          isPublic: item.playlist.is_public,
          permission: item.permission_type,
          isShared: true,
          ownerId: item.playlist.user_id,
          owner: ownerData,
          songs: (item.playlist.playlist_tracks || []).map(track => ({
            id: track.id,
            title: track.track_title,
            artist: track.track_artist,
            image: track.track_image,
            previewUrl: track.track_preview_url,
            spotifyUrl: track.track_external_url,
            position: track.position
          })),
          created_at: item.playlist.created_at,
          updated_at: item.playlist.updated_at
        };
      })
    );

    console.log('✅ [Service] Playlists transformadas:', playlistsWithOwners);
    return playlistsWithOwners;
  },

  // Obtener playlists públicas
  async getPublicPlaylists(limit = 20, offset = 0) {
    const { data, error } = await supabase
      .from('playlist')
      .select(`
        *,
        playlist_tracks (
          id,
          track_title,
          track_artist,
          track_image,
          track_preview_url,
          track_external_url,
          position,
          created_at
        ),
        profiles:user_id (
          id,
          email,
          username,
          avatar_url
        )
      `)
      .eq('is_public', true)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) throw error;

    return data.map(playlist => ({
      id: playlist.id,
      name: playlist.name,
      description: playlist.description,
      image: playlist.image_url,
      isPublic: playlist.is_public,
      owner: playlist.profiles,
      songs: playlist.playlist_tracks.map(track => ({
        id: track.id,
        title: track.track_title,
        artist: track.track_artist,
        image: track.track_image,
        previewUrl: track.track_preview_url,
        spotifyUrl: track.track_external_url,
        position: track.position
      })),
      created_at: playlist.created_at,
      updated_at: playlist.updated_at
    }));
  }
};
